import { Link } from "react-router-dom";
import {
  Sparkles, Wallet, PlaneTakeoff, MapPin, Briefcase, ArrowRight,
  Bookmark, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BandPill } from "@/components/admission/BandPill";
import GradeConversionMissingBadge from "@/components/app/GradeConversionMissingBadge";
import TrueAnnualCostBar from "@/components/admission/TrueAnnualCostBar";
import { SubBandChip, ConfidenceTierChip } from "@/components/admission/SubBandChip";
import { AdmitProbabilityChip, publishedRateLabel } from "@/components/admission/AdmitProbabilityChip";
import { SameScoreTag } from "@/components/admission/SameScoreTag";
import { RowActions } from "@/components/admission/RowActions";

// One row in the Quick Check results list. Renders the compact card
// header (university + degree link + chance badge + Explain button)
// and, when the applicant has clicked Explain, the AI narrative Card
// with score breakdown + "Now what?" next-steps.
//
// The parent owns every state hook (narratives, loading id, shortlist
// ids, gap fixer). This component is a pure view over a single row.
export default function ResultRow({
  r,
  narrative,
  isLoading,
  onExplain,
  shortlistedIds,
  onAddToShortlist,
  onRemoveFromShortlist,
  onFixGap,
  user,
  onSignIn,
  costScaleMax = 0,
  highlightSalary = false,        // sort-by-salary → show the $ value inline
  highlightAcceptance = false,    // sort-by-acceptance → show the % inline
  highlightCompetitive = false,   // sort-by-competitive → show the index / not-sourced chip
  sameScore = false,              // exact Compete tie with the row above/below
  shortlistBusy = false,
  onToggleShortlist,
  compared = false,
  compareFull = false,
  onToggleCompare,
}) {
  const rowId = r.degree_id || r.university_id;

  return (
    <div>
      <div
        data-testid={`result-row-${rowId}`}
        className="flex flex-wrap items-center justify-between gap-3 border border-stone-200 rounded-lg p-4 bg-white lift"
      >
        <div>
          <div className="font-editorial text-lg leading-tight">
            {r.university_name}
            {r.degree_name && r.degree_id && (
              <> — <Link
                data-testid={`degree-link-${r.degree_id}`}
                to={`/degree/${r.degree_id}`}
                className="text-stone-500 hover:text-stone-900 hover:underline underline-offset-4"
              >{r.degree_name}</Link></>
            )}
          </div>
          <div className="text-xs label-uppercase mt-1 flex items-center gap-2 flex-wrap">
            <span>{r.country}{r.campus ? ` · ${r.campus}` : ""} · gap {r.classification.gap_score}</span>
            {r.classification.sub_band && (
              <SubBandChip
                classification={r.classification}
                rowId={rowId}
              />
            )}
            {r.classification.confidence_tier?.tier && r.classification.confidence_tier.tier !== "quantitative" && (
              <ConfidenceTierChip
                tier={r.classification.confidence_tier}
                rowId={rowId}
              />
            )}
            {highlightSalary && (
              r.median_earnings_1yr_usd != null ? (
                <span
                  data-testid={`salary-chip-${rowId}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-300 text-emerald-800 normal-case tracking-normal"
                >
                  1-yr grad salary&nbsp;<span className="font-mono font-semibold">${(r.median_earnings_1yr_usd || 0).toLocaleString()}</span>
                </span>
              ) : (
                <span
                  data-testid={`salary-chip-none-${rowId}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100 border border-stone-300 text-stone-600 normal-case tracking-normal"
                  title="No sourced graduate salary — this course sinks to the bottom of a salary sort."
                >
                  Salary — not sourced
                </span>
              )
            )}
            {highlightAcceptance && (
              r.acceptance_rate != null ? (
                <span
                  data-testid={`accept-chip-${rowId}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-300 text-emerald-800 normal-case tracking-normal"
                >
                  Accept&nbsp;<span className="font-mono font-semibold">{Math.round((r.acceptance_rate || 0) * (r.acceptance_rate > 1 ? 1 : 100))}%</span>
                </span>
              ) : (
                <span
                  data-testid={`accept-chip-none-${rowId}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100 border border-stone-300 text-stone-600 normal-case tracking-normal"
                  title="Acceptance rate not published — course sinks to the bottom of this sort."
                >
                  Accept — not sourced
                </span>
              )
            )}
            {highlightCompetitive && (
              /* The Compete score is the sort key, so it is always the first chip; offer-rate chips are its inputs. */
              r.competitiveness_index != null ? (
                <span
                  data-testid={`compete-chip-${rowId}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-300 text-indigo-800 normal-case tracking-normal"
                  title={(() => {
                    if (r.competitiveness_source?.startsWith("tariff_and_")) {
                      const acc = r.competitiveness_source.replace("tariff_and_", "");
                      if (acc === "ucas_eoc_uni_modelled_offer") {
                        return "Blend of UK HESA tariff (40%) and 1 − yield-corrected offer rate estimate (60%). Estimate — a real subject-level number needs UCAS EXACT.";
                      }
                      if (acc === "ucas_eoc_uni") {
                        return "Blend of UK HESA tariff (40%) and 1 − UCAS End of Cycle uni-level acceptance rate (60%). Uni-average — not subject-precise.";
                      }
                      return "Blend of UK HESA tariff (40%) and 1 − legacy uni acceptance rate (60%). Uni-average — not subject-precise.";
                    }
                    if (r.competitiveness_source === "tariff") {
                      return "UK HESA typical UCAS tariff (subject-level). Not a league-table position.";
                    }
                    if (r.competitiveness_source === "ucas_eoc_uni_modelled_offer") {
                      return "Yield-corrected offer rate estimate from UCAS End of Cycle acceptance rate. Est. only.";
                    }
                    if (r.competitiveness_source === "ucas_eoc_uni") {
                      return "UCAS End of Cycle uni-level acceptance rate. Uni-average — not subject-precise.";
                    }
                    return "1 − uni acceptance rate. Not a subject-league ranking.";
                  })()}
                >
                  Compete&nbsp;<span className="font-mono font-semibold">{Math.round(r.competitiveness_index * 100)}</span>
                </span>
              ) : (
                <span
                  data-testid={`compete-chip-none-${rowId}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100 border border-stone-300 text-stone-600 normal-case tracking-normal"
                  title="No UK entry tariff and no acceptance rate published — course sinks to the bottom of this sort."
                >
                  Ranking — not sourced
                </span>
              )
            )}
            {highlightCompetitive && sameScore && <SameScoreTag r={r} testId={`same-score-tag-${rowId}`} />}
            {highlightCompetitive && r.subject_offer_rate != null && r.subject_offer_source_url && r.subject_offer_cycle && (
              /* Only a sourced course-level UCAS offer rate (offers ÷ applications) with its cycle; modelled uni offer rates are never shown. */
              <a
                href={r.subject_offer_source_url} target="_blank" rel="noopener noreferrer"
                data-testid={`subject-offer-chip-${rowId}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-100 border border-indigo-400 text-indigo-900 normal-case tracking-normal"
                title={`Published course offer rate: offers ÷ applications, UCAS ${r.subject_offer_cycle} cycle. Not your personal chance.`}
              >
                Course offer rate&nbsp;<span className="font-mono font-semibold">{Math.round(r.subject_offer_rate * 100)}%</span>
              </a>
            )}
            {highlightCompetitive && r.top_tariff_intake_pct != null && (
              /* Companion selectivity chip — real, course-level, HESA
                 % of admitted students with A*AA+ tariff. Renders
                 alongside the Compete chip whenever the signal is
                 published for the course, so a family scanning the
                 row sees a subject-precise number even when we don't
                 yet have UCAS EXACT's offer rate. */
              <span
                data-testid={`top-tariff-chip-${rowId}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-300 text-indigo-800 normal-case tracking-normal"
                title={`HESA: ${Math.round(r.top_tariff_intake_pct * 100)}% of admitted students arrived with A*AA+ tariff (n=${r.top_tariff_intake_sample_size}). Selectivity signal — not an offer rate.`}
              >
                Top intake&nbsp;<span className="font-mono font-semibold">{Math.round(r.top_tariff_intake_pct * 100)}%</span>
              </span>
            )}
            {r.data_confidence === "verified" ? (
              <span data-testid={`confidence-${rowId}`}
                    title="This course's own entry requirements are on file and were used for the grade match. It describes the data used, not a verified prediction."
                    className="inline-flex items-center gap-1 text-emerald-700">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" />
                course entry data
              </span>
            ) : (
              <span data-testid={`confidence-${rowId}`}
                    title="No entry requirements are on file for this course, so the grade match uses the university's general requirements."
                    className="inline-flex items-center gap-1 text-stone-500">
                <span className="h-1.5 w-1.5 rounded-full bg-stone-400" />
                uni-level entry data
              </span>
            )}
            {r.mandatory_semester_fee_usd != null && (
              <span
                data-testid={`semester-fee-hint-${rowId}`}
                className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 normal-case"
                title={r.semester_fee_covers || ""}
              >
                {r.eu_only_free ? "EU-free · non-EU pays" : "Tuition free"} · ${r.mandatory_semester_fee_usd}/yr fees
              </span>
            )}
          </div>
          <TrueAnnualCostBar
            trueCost={r.true_cost}
            scaleMax={costScaleMax}
            testid={`true-cost-bar-${rowId}`}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {r.classification.grade_incomparable && (
            <GradeConversionMissingBadge
              compact
              applicantCurriculum={r.classification.applicant_curriculum}
            />
          )}
          <div className="flex flex-col items-center gap-0.5" data-testid={`grade-match-${rowId}`}>
            <span className="text-[9px] uppercase tracking-wider text-stone-500">Grade match · rule-based</span>
            <BandPill bucket={r.classification.bucket} label={r.classification.label} rowId={rowId} />
          </div>
          {r.classification.admit_probability?.published_rate && (
            <div className="flex flex-col items-center gap-0.5" data-testid={`est-chance-${rowId}`}>
              <span className="text-[9px] uppercase tracking-wider text-stone-500">{publishedRateLabel(r.classification.admit_probability.published_rate)}</span>
              <AdmitProbabilityChip admitProbability={r.classification.admit_probability} rowId={rowId} />
            </div>
          )}
          <Button
            variant={narrative ? "outline" : "default"}
            size="sm"
            data-testid={`explain-btn-${rowId}`}
            onClick={() => onExplain(r.university_id, rowId, r.degree_id)}
            disabled={isLoading}
            className={
              narrative
                ? ""
                : "bg-[#4C1D95] hover:bg-[#3b1479] text-white shadow-sm"
            }
          >
            {isLoading ? (
              <><Sparkles className="h-3.5 w-3.5 mr-1.5 animate-pulse" /> Thinking…</>
            ) : narrative ? (
              <>Hide explanation</>
            ) : (
              <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Why this fit?</>
            )}
          </Button>
        </div>
        {onToggleShortlist && (
          <RowActions
            r={r}
            rowId={rowId}
            shortlisted={shortlistedIds.has(rowId)}
            shortlistBusy={shortlistBusy}
            onToggleShortlist={onToggleShortlist}
            compared={compared}
            compareFull={compareFull}
            onToggleCompare={onToggleCompare}
          />
        )}
      </div>

      {isLoading && (
        <div data-testid={`explain-loading-${rowId}`}
             className="mt-2 ml-4 text-xs text-stone-500 font-mono">
          WhichUniversityAdvisor AI is thinking…
        </div>
      )}

      {narrative && (
        <Card data-testid={`ai-narrative-${rowId}`}
              className="mt-2 p-5 border-stone-200 bg-white border-l-4 border-l-[#4C1D95]">
          <div className="label-uppercase mb-2">
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> WhichUniversityAdvisor AI
            </span>
          </div>
          {narrative.classification?.grade_incomparable && (
            <div className="mb-3" data-testid={`narrative-conversion-hint-${rowId}`}>
              <GradeConversionMissingBadge
                applicantCurriculum={narrative.classification.applicant_curriculum}
              />
            </div>
          )}
          <p className="text-sm leading-relaxed text-stone-700 whitespace-pre-wrap">
            {narrative.narrative}
          </p>
          <div className="mt-4 pt-3 border-t border-stone-100">
            <div className="flex items-baseline justify-between mb-2">
              <div className="label-uppercase !text-[10px]">
                {narrative.classification?.audition_gated
                  ? `Score breakdown vs typical ${narrative.classification.entry_gate === "portfolio" ? "portfolio" : "audition"} entrant`
                  : "Score breakdown vs typical entrant"}
              </div>
              <div className="text-[10px] text-stone-500">
                +1 well above · 0 meets · −1 well below
              </div>
            </div>
            {narrative.classification?.audition_gated && (
              <div
                data-testid={`audition-gated-note-${rowId}`}
                className="mb-3 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5"
              >
                Grades / subjects / test / English don't gate this course —
                the {narrative.classification.entry_gate} does. Only
                <strong> portfolio / interview</strong> and
                <strong> activities</strong> contribute to your score here.
              </div>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 text-xs">
              {Object.entries(narrative.classification.breakdown).map(([k, v]) => {
                const num = Number(v);
                const isBelow = num < -0.15;
                const isAuditionInert = narrative.classification?.audition_gated
                  && ["grades", "subjects", "test_score", "english"].includes(k);
                const cls = isAuditionInert ? "text-stone-400"
                           : num > 0.15 ? "text-emerald-700"
                           : isBelow ? "text-red-700"
                           : "text-stone-500";
                const label = isAuditionInert ? "n/a"
                              : num > 0.15 ? "above"
                              : isBelow ? "below"
                              : "meets";
                return (
                  <div key={k} data-testid={`breakdown-${k}`}>
                    <div className="label-uppercase text-[9px]">{k.replace("_", " ")}</div>
                    <div className={`font-mono font-semibold ${cls}`}>
                      {isAuditionInert ? "—" : (num > 0 ? "+" : "") + num.toFixed(2)}
                    </div>
                    <div className={`text-[9px] uppercase tracking-wider ${cls}`}>{label}</div>
                    {isBelow && !isAuditionInert && (
                      <button
                        type="button"
                        data-testid={`fix-gap-btn-${k}`}
                        onClick={() => onFixGap({
                          gapKey: k,
                          gapValue: num,
                          degreeId: r.degree_id,
                          degreeName: r.degree_name,
                          universityName: r.university_name,
                        })}
                        className="mt-1 text-[10px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline">
                        fix this →
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Now what? — surface the 4 remaining questions right here. */}
          <div className="mt-5 pt-4 border-t border-stone-100">
            <div className="label-uppercase !text-[10px] text-[#4C1D95]">Now…</div>
            <ol className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <li className="flex items-start gap-2 text-stone-700">
                <Wallet className="h-4 w-4 text-[#4C1D95] mt-0.5 shrink-0" />
                <span><span className="font-semibold">Can you afford it?</span> — tuition, housing, food, insurance, visa & fee growth vs your family budget.</span>
              </li>
              <li className="flex items-start gap-2 text-stone-700">
                <PlaneTakeoff className="h-4 w-4 text-[#4C1D95] mt-0.5 shrink-0" />
                <span><span className="font-semibold">Can you get the visa?</span> — passport & residence-aware sponsor licence and financial-evidence checks.</span>
              </li>
              <li className="flex items-start gap-2 text-stone-700">
                <MapPin className="h-4 w-4 text-[#4C1D95] mt-0.5 shrink-0" />
                <span><span className="font-semibold">Will you enjoy living there?</span> — campus, city, climate, distance from home, diversity.</span>
              </li>
              <li className="flex items-start gap-2 text-stone-700">
                <Briefcase className="h-4 w-4 text-[#4C1D95] mt-0.5 shrink-0" />
                <span><span className="font-semibold">Will it take you where you want?</span> — accreditation, placement, PSW, employer signal.</span>
              </li>
            </ol>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Button
                data-testid={`narrative-add-shortlist-${rowId}`}
                variant="outline" size="sm"
                onClick={() => {
                  const isOn = shortlistedIds.has(r.degree_id || r.university_id);
                  if (isOn) onRemoveFromShortlist?.(r.university_id, r.university_name, r.degree_id, r.degree_name);
                  else onAddToShortlist(r.university_id, r.university_name, r.degree_id, r.degree_name);
                }}
                title={shortlistedIds.has(r.degree_id || r.university_id) ? "Click to remove from your shortlist" : "Add to shortlist"}
                className={shortlistedIds.has(r.degree_id || r.university_id)
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  : ""}>
                {shortlistedIds.has(r.degree_id || r.university_id) ? (
                  <><Check className="h-3.5 w-3.5 mr-1.5 text-emerald-700" /> Shortlisted · click to remove</>
                ) : (
                  <><Bookmark className="h-3.5 w-3.5 mr-1.5" /> Shortlist this degree</>
                )}
              </Button>
              {user ? (
                <Link to={
                  r.degree_id
                    ? `/advanced?focus=${encodeURIComponent(r.degree_id)}`
                    : r.university_id
                      ? `/advanced?focus_uni=${encodeURIComponent(r.university_id)}`
                      : "/advanced"
                }>
                  <Button data-testid={`narrative-advanced-${rowId}`}
                          className="h-auto py-2 px-4 bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md">
                    <span className="flex flex-col items-start leading-tight text-left">
                      <span className="text-sm font-medium flex items-center">
                        See the full picture <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                      </span>
                      <span className="text-[10px] font-underground-sc tracking-wider opacity-80 mt-0.5">
                        cost · visa · life · career fit
                      </span>
                    </span>
                  </Button>
                </Link>
              ) : (
                <Button data-testid={`narrative-signin-${rowId}`} onClick={onSignIn}
                        className="h-auto py-2 px-4 bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md">
                  <span className="flex flex-col items-start leading-tight text-left">
                    <span className="text-sm font-medium flex items-center">
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Sign in for the full picture
                    </span>
                    <span className="text-[10px] font-underground-sc tracking-wider opacity-80 mt-0.5">
                      cost · visa · life · career fit
                    </span>
                  </span>
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
